import { useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Box, Typography } from '@mui/material';

/**
 * Thin wrapper around MaterialReactTable with the conventions used across the
 * Customer 365 tabs: compact, outlined, no top toolbar, pagination only once a
 * list grows past a page, and a friendly empty-state fallback. Keeps each tab
 * terse and the tables visually consistent.
 *
 * Sorting is controlled here. Passing only `state: { isLoading }` into MRT
 * can drop header-click sort on the next render.
 */
export default function DataTable({
  columns,
  data,
  isLoading = false,
  emptyText = 'No records.',
  initialState,
  ...rest
}) {
  const rows = Array.isArray(data) ? data : [];
  const paginate = rows.length > 10;
  const [sorting, setSorting] = useState(() => initialState?.sorting ?? []);
  const { state: restState, ...restRest } = rest;

  const table = useMaterialReactTable({
    columns,
    data: rows,
    enableSorting: true,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableColumnActions: false,
    enableRowNumbers: false,
    enableTopToolbar: false,
    enableBottomToolbar: paginate,
    enablePagination: paginate,
    initialState: { density: 'compact', pagination: { pageSize: 10, pageIndex: 0 }, ...initialState },
    muiTablePaperProps: { elevation: 0, variant: 'outlined', sx: { borderRadius: 2 } },
    muiTableContainerProps: { sx: { maxHeight: '60vh' } },
    renderEmptyRowsFallback: () => (
      <Box p={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
      </Box>
    ),
    ...restRest,
    onSortingChange: setSorting,
    state: { isLoading: !!isLoading, sorting, ...restState },
  });

  return <MaterialReactTable table={table} />;
}
